
import {
  getInsurances,
  getIPANumbers,
  getLoginSummary
} from '@/components/insurances/insurance.service'
import {
  getInsuranceSupervisorInsurances,
  getUserInsurances
} from '@/components/doctors/doctors.service'
import { InsuranceIPANumber, PaginationVM, Role, SelectVM } from '@/models'
import { Component, Vue, Watch } from 'vue-property-decorator'
import {
  ensureCurrentUserInsurances,
  getUserRole,
  getUserRmtId
} from '../admin/users/users.service'
import moment from 'moment'
import xlsx from 'json-as-xlsx'
import Multiselect from 'vue-multiselect'
import { getResolve } from '../doctors/doctors.service'

@Component({
  components: {
    Multiselect
  }
})
export default class LoginSummary extends Vue {
  public userRole = getUserRole()
  public roles: SelectVM[] = []
  public insurances: SelectVM[] = []
  public selectedInsurances: SelectVM[] = []
  public ipaNumbers: SelectVM[] = []
  public selectedIPANumbers: SelectVM[] = []
  public ipaNumberList: InsuranceIPANumber[] = []
  /** Assigned InsuranceIPA labels for InsuranceSupervisor. */
  public supervisorAssignedIPAs: string[] = []
  public filtersReady = false
  public statusList = [
    {
      label: 'Active',
      value: true
    },
    {
      label: 'Inactive',
      value: false
    }
  ]
  public twoFactorList = [
    {
      label: 'Set',
      value: true
    },
    {
      label: 'Not Set',
      value: false
    }
  ]

  public selectedStatusFilter: {
    label: string
    value: boolean
  } | null = null
  public selectedTwoFactorFilter: {
    label: string
    value: boolean
  } | null = null

  public selectedGender: string | null = null
  public selectedRole: string | null = null
  public selectedRoleFilter: SelectVM | null = null

  public fields = [
    { key: 'index', label: 'Index', sortable: true, order: 1 },
    { key: 'userName', label: 'User', sortable: true, order: 2 },
    { key: 'fullName', label: 'Full Name', sortable: true, order: 3 },
    { key: 'email', label: 'Email', sortable: true, order: 3 },

    { key: 'rolName', label: 'Role', sortable: true, order: 4 },
    {
      key: 'loginStatus',
      label: 'User Status',
      sortable: true,
      order: 5,
      formatter: 'formatActive'
    },
    {
      key: 'firstLogin',
      label: 'First Login',
      sortable: true,
      order: 6,
      formatter: 'formatDateTime'
    },
    {
      key: 'lastLogin',
      label: 'Last Login',
      sortable: true,
      order: 7,
      formatter: 'formatDateTime'
    },
    {
      key: 'policyAcceptanceDate',
      label: 'Policy Acceptance Date',
      sortable: true,
      order: 8,
      formatter: 'formatDate'
    },
    // { key: 'daysafter', label: 'Days After', sortable: true, order: 8 },
    { key: 'mtdLogin', label: 'MTD Login', sortable: true, order: 9 },
    { key: 'qtdLogin', label: 'QTD Login', sortable: true, order: 10 },
    {
      key: 'prevSemesterLogin',
      label: 'Prev SM',
      sortable: true,
      order: 11
    },
    {
      key: 'semesterLogin',
      label: 'Current SM',
      sortable: true,
      order: 12
    },
    { key: 'ytdLogin', label: 'YTD Login', sortable: true, order: 13 },
    { key: 'alL_Login', label: 'All Login', sortable: true, order: 14 },
    {
      key: 'current2FA',
      label: 'Current 2FA',
      sortable: true,
      order: 15,
      formatter: 'formatBool'
    },
    {
      key: 'successLoginRate',
      label: 'Success Login Rate MTD',
      sortable: true,
      order: 16,
      formatter: 'formatPercentage'
    }
  ]
  public currentUser = ''
  public items = []
  public perPage = 10
  public pageOptions = [10, 20, 50, 100, 250, 500, 1000]
  public pagination = {} as PaginationVM
  public searchQuery = ''
  public showSearch = false
  // Filters align with General/BFS/BSS reports for Insurance/IPA.
  // Role/2FA are Login Summary–only.
  get showInsuranceFilter() {
    return (
      this.userRole === Role.Insurance ||
      this.userRole === Role.InsuranceStaff ||
      this.userRole === Role.InsuranceSupervisor ||
      this.userRole === Role.Admin ||
      this.userRole === Role.Superuser
    )
  }
  get showIpaFilter() {
    return (
      this.userRole === Role.Insurance ||
      this.userRole === Role.InsuranceSupervisor ||
      this.userRole === Role.Admin ||
      this.userRole === Role.Superuser
    )
  }
  get showRoleFilter() {
    // Insurance / Supervisor: scoped by RMT_UserID, no Role/2FA filters.
    // InsuranceStaff keeps Role=Doctor (legacy Login Summary behavior).
    return (
      this.userRole !== Role.Insurance &&
      this.userRole !== Role.InsuranceSupervisor
    )
  }
  get hasActiveFilters() {
    return !!(
      this.searchQuery ||
      this.selectedStatusFilter ||
      this.selectedTwoFactorFilter ||
      this.selectedRoleFilter ||
      (this.selectedInsurances && this.selectedInsurances.length) ||
      (this.selectedIPANumbers && this.selectedIPANumbers.length)
    )
  }
  private async created() {
    this.userRole = getUserRole()
    await this.loadReportFilters()
    await this._getLoginSummary()
  }
  @Watch('perPage')
  perPageChange() {
    this._getLoginSummary()
  }
  @Watch('selectedInsurances', { deep: true })
  onSelectedInsurancesChange() {
    this.selectedIPANumbers = []
    this.refreshIpaOptions()
  }
  get _pagination() {
    return this.pagination
  }
  public async resetFilters() {
    this.searchQuery = ''
    this.selectedRoleFilter = null
    this.selectedStatusFilter = null
    this.selectedTwoFactorFilter = null
    this.selectedInsurances = []
    this.selectedIPANumbers = []
    this.refreshIpaOptions()
    await this._getLoginSummary(1)
  }
  public async onPageChange(pageNumber: number) {
    await this._getLoginSummary(pageNumber, this.searchQuery)
  }
  public async filter() {
    await this._getLoginSummary(1, this.searchQuery)
  }
  public async cleanFilter() {
    this.searchQuery = ''
    await this._getLoginSummary(1, this.searchQuery)
  }
  private getInsuranceCsv(): string {
    if (!this.showInsuranceFilter) return ''
    return [
      ...new Set(
        (this.selectedInsurances || [])
          .map(i => String(i.value).trim())
          .filter(Boolean)
      )
    ].join(',')
  }
  private getIpaCsv(): string {
    if (!this.showIpaFilter) return ''
    return [
      ...new Set(
        (this.selectedIPANumbers || [])
          .map(i => String(i.value).trim())
          .filter(Boolean)
      )
    ].join(',')
  }
  private refreshIpaOptions() {
    if (!this.showIpaFilter) {
      this.ipaNumbers = []
      return
    }
    let scopeIds = this.getInsuranceCsv()
      .split(',')
      .map(x => x.trim())
      .filter(Boolean)
    if (!scopeIds.length && this.insurances?.length) {
      scopeIds = [
        ...new Set(this.insurances.map(i => String(i.value)).filter(Boolean))
      ]
    }
    const assignedIpa = new Set(
      this.userRole === Role.InsuranceSupervisor
        ? this.supervisorAssignedIPAs || []
        : []
    )
    this.ipaNumbers = (this.ipaNumberList || [])
      .filter(c => {
        const inScope =
          scopeIds.length === 0 || scopeIds.includes(String(c.insuranceID))
        const assignedOk =
          assignedIpa.size === 0 || assignedIpa.has(c.insuranceIPA)
        return inScope && assignedOk
      })
      .map(c => ({
        label: c.insuranceIPA,
        value: c.insuranceIPA
      }))
  }
  private async loadAssignedUserInsurances(catalog: SelectVM[]) {
    await ensureCurrentUserInsurances()
    const rmtUserId = Number(getUserRmtId())
    const assigned = rmtUserId ? await getUserInsurances(rmtUserId) : []
    const idSet = new Set(
      (assigned || []).map(a => String(a.insuranceID)).filter(Boolean)
    )
    this.insurances = catalog.filter(c => idSet.has(String(c.value)))
  }
  private async loadReportFilters() {
    if (this.showRoleFilter) {
      const response = await getResolve()
      let roles = response?.roles || []
      if (this.userRole === Role.InsuranceStaff) {
        roles = roles.filter(c => c.label == 'Doctor')
      }
      this.roles = roles
    } else {
      this.roles = []
    }

    if (!this.showInsuranceFilter) {
      this.filtersReady = true
      return
    }

    const insuranceResponse = await getInsurances(1, 1000)
    const catalog = (insuranceResponse?.data || []).map(c => ({
      label: c.insuranceName,
      value: c.insuranceID
    }))

    if (
      this.userRole === Role.Insurance ||
      this.userRole === Role.InsuranceStaff
    ) {
      // Same as Patients / other reports: only assigned insurers.
      await this.loadAssignedUserInsurances(catalog)
    } else if (this.userRole === Role.InsuranceSupervisor) {
      const rmtUserId = Number(getUserRmtId())
      const assigned = rmtUserId
        ? await getInsuranceSupervisorInsurances(rmtUserId)
        : []
      const idSet = new Set(
        (assigned || []).map(a => String(a.insuranceID)).filter(Boolean)
      )
      this.supervisorAssignedIPAs = [
        ...new Set(
          (assigned || []).map(a => a.insuranceIPA).filter(Boolean) as string[]
        )
      ]
      this.insurances = catalog.filter(c => idSet.has(String(c.value)))
    } else {
      // Admin / Superuser: full catalog
      this.insurances = catalog
    }

    if (this.showIpaFilter) {
      const ipaNumbersResponse = await getIPANumbers()
      this.ipaNumberList = ipaNumbersResponse?.data || []
      this.refreshIpaOptions()
    }
    this.filtersReady = true
  }
  public async _getLoginSummary(pageNumber = 1, searchQuery = '') {
    const roleName = this.showRoleFilter
      ? this.selectedRoleFilter?.label
      : undefined
    const twoFA = this.showRoleFilter
      ? this.selectedTwoFactorFilter?.value
      : undefined

    const res = await getLoginSummary(
      {
        pageNumber,
        pageSize: this.perPage,
        searchQuery: searchQuery || this.searchQuery,
        roleName,
        active: this.selectedStatusFilter?.value,
        twoFA
      },
      this.getIpaCsv(),
      this.getInsuranceCsv()
    )

    this.items = Array.isArray(res?.data) ? res.data : []
    const paginationHeader = res?.headers?.['x-pagination']
    this.pagination = paginationHeader
      ? JSON.parse(paginationHeader)
      : ({} as PaginationVM)
  }
  public formatStatus(value: boolean) {
    if (value) {
      return 'Success'
    } else if (value == false) {
      return 'Fail'
    }
  }
  public formatBool(value: boolean) {
    if (value) {
      return 'Yes'
    } else if (value == false) {
      return 'No'
    }
  }
  public formatActive(value: boolean) {
    if (value) {
      return 'Active'
    } else if (value == false) {
      return 'Inactive'
    }
  }
  public formatPercentage(value: string) {
    const percentage = value ? +(+value * 100).toFixed(2) + '%' : 0 + '%'
    return percentage
  }
  public async exportTable() {
    let items = []
    const params = {
      pageNumber: 1,
      pageSize: 999999,
      searchQuery: this.searchQuery,
      roleName: this.showRoleFilter
        ? this.selectedRoleFilter?.label
        : undefined,
      active: this.selectedStatusFilter?.value,
      twoFA: this.showRoleFilter
        ? this.selectedTwoFactorFilter?.value
        : undefined
    }
    const response = await getLoginSummary(
      params,
      this.getIpaCsv(),
      this.getInsuranceCsv()
    )
    items = response.data

    const data = [
      {
        sheet: 'Login Summary',
        columns: [],
        content: []
      }
    ]

    data[0].columns = this.fields
      .map(c => {
        return {
          label: c.label,
          value: c.key
        }
      })
      .filter(c => c.value != 'index')
    data[0].content = items.map(c => {
      return {
        ...c,
        loginStatus: this.formatActive(c.loginStatus),
        current2FA: this.formatBool(c.current2FA),
        firstLogin: this.formatDateTime(c.firstLogin),
        lastLogin: this.formatDateTime(c.lastLogin),
        successLoginRate: this.formatPercentage(c.successLoginRate)
      }
    })

    const settings = {
      fileName: 'Login Summary Report',
      extraLength: 3,
      writeMode: 'writeFile',
      writeOptions: {},
      RTL: false
    }
    xlsx(data, settings)
  }
  public formatMoney(value) {
    return new Intl.NumberFormat('us-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value)
  }

  public formatDateTime(value: string) {
    if (value) return moment(value).format('MM/DD/YYYY HH:mm')
  }
  public formatDate(value: string) {
    if (value) return moment(value).format('MM/DD/YYYY')
  }
}
